import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";

import { Autoplay, Pagination } from "swiper/modules";
import styled from "styled-components";
import colors from "theme/colors";
import { Calendar, ChevronLeft, ChevronRight, Flame, Heart, Ticket } from 'lucide-react';
import { useEventDetailModal } from "context/events/EventDetailModal";
import useFavorite from 'hooks/useFavorite';
import { eventWhen, eventPlace, eventPrice, PLACEHOLDER_EVENT } from 'theme/discover';
import {
  HeroCarouselWrapper,
  HeroCarouselViewport,
  heroSwiperSkin,
  heroBreakpoints,
  HeroArrow,
  HeroCard,
  HeroScrim,
  HeroCorner,
  HeroBadge,
  HeroGlassButton,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroMeta,
} from 'components/commons/HeroCard';

const CarouselFavoriteButton = ({ slug, initialFavorited, eventName }) => {
    const { isFavorited, toggleFavorite } = useFavorite(slug, initialFavorited);
    return (
        <HeroGlassButton
            type='button'
            aria-pressed={isFavorited}
            aria-label={isFavorited
                ? `Remover ${eventName || 'evento'} dos favoritos`
                : `Favoritar ${eventName || 'evento'}`}
            onClick={(e) => {
                e.stopPropagation();
                toggleFavorite();
            }}
        >
            <Heart
                size={16}
                color={isFavorited ? colors.occasionalPink : '#FFFFFF'}
                fill={isFavorited ? colors.occasionalPink : 'none'}
            />
        </HeroGlassButton>
    );
};

/**
 * Destaques no desenho do app (WUIHeroCard): a foto ocupa o cartão inteiro,
 * com o selo "Em alta", o título e as linhas de data e preço em branco sobre
 * um degradê escuro.
 */
const EventCarousel = ({ data }) => {
  const { setEventDetailModal } = useEventDetailModal();
  const swiperRef = useRef(null);

  // WCAG 2.2.2 (Pause, Stop, Hide): movimento automático acima de 5s precisa ser
  // pausável. Quem pediu menos animação no sistema não recebe autoplay nenhum.
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Abaixo de 6 cards o Swiper não tem slides suficientes para girar, e a faixa
  // vira uma lista parada — a de destaques pode legitimamente chegar com quatro
  // eventos, o mínimo que o backend garante antes de ampliar a busca. Os três
  // comportamentos do modo giratório (loop, centralização e realce do card
  // central) pressupõem movimento, então caem juntos: parados, os cards alinham
  // à esquerda, preenchem a linha e têm todos o mesmo tamanho.
  const canLoop = data.length >= 6;

  const handleNext = () => swiperRef.current?.slideNext();
  const handlePrev = () => swiperRef.current?.slidePrev();

  const handleSlideClick = (item, index) => {
    // Trazer o card para o centro antes de abrir só se protege de clique errado
    // enquanto a faixa gira. Parada, os três cards estão inteiros na tela e com o
    // mesmo peso visual: exigir dois cliques nos laterais seria só um obstáculo.
    if (!canLoop || swiperRef.current?.realIndex === index) {
      setEventDetailModal(item);
      return;
    }
    swiperRef.current?.slideToLoop(index, 500);
  };

  return (
    <HeroCarouselWrapper>
      <HeroArrow $side='left' onClick={handlePrev} aria-label='Destaque anterior'>
        <ChevronLeft size={18} />
      </HeroArrow>
      <HeroArrow $side='right' onClick={handleNext} aria-label='Próximo destaque'>
        <ChevronRight size={18} />
      </HeroArrow>

      <HeroCarouselViewport>
        <StyledSwiper
          $emphasis={canLoop}
          slidesPerView={3}
          spaceBetween={14}
          centeredSlides={canLoop}
          loop={canLoop}
          breakpoints={heroBreakpoints(canLoop)}
          autoplay={prefersReducedMotion ? false : {
            delay: 6000,
            // Parar ao interagir e ao passar o mouse: sem isso o slide se move
            // debaixo do cursor no instante do clique e o usuário abre o evento errado.
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            clickable: true,
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          modules={[Autoplay, Pagination]}
        >
          {data.map((item, index) => {
            const price = eventPrice(item);

            return (
              <SwiperSlide key={item?.slug || item?.id || index} onClick={() => handleSlideClick(item, index)}>
                <HeroCard>
                  <img
                    src={item?.images?.[0] || PLACEHOLDER_EVENT}
                    alt={item?.name}
                    onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_EVENT; }}
                  />
                  <HeroScrim />

                  <HeroCorner $side='left'>
                    <HeroBadge>
                      <Flame size={12} />
                      Em alta
                    </HeroBadge>
                  </HeroCorner>

                  <HeroCorner $side='right'>
                    <CarouselFavoriteButton slug={item?.slug} initialFavorited={item?.is_favorited} eventName={item?.name} />
                  </HeroCorner>

                  <HeroContent>
                    <HeroTitle>{item?.name}</HeroTitle>
                    <HeroSubtitle>{eventPlace(item)}</HeroSubtitle>
                    <HeroMeta>
                      <span>
                        <Calendar size={12} />
                        {eventWhen(item)}
                      </span>
                      {!!price && (
                        <span>
                          <Ticket size={12} />
                          {price}
                        </span>
                      )}
                    </HeroMeta>
                  </HeroContent>
                </HeroCard>
              </SwiperSlide>
            );
          })}
        </StyledSwiper>
      </HeroCarouselViewport>

    </HeroCarouselWrapper>
  );
};

const StyledSwiper = styled(Swiper)`
  ${heroSwiperSkin}
`;

export default EventCarousel;
